import { ReactNode, useEffect, useRef } from 'react';
import { Jodit } from 'jodit/esm/index';
import 'jodit/esm/plugins/all';
import 'jodit/es2021/jodit.min.css';
import type { Config } from 'jodit/esm/config';
import type { DeepPartial, IControlType, IJodit } from 'jodit/esm/types';
import ErrorText from '../ErrorText';
import Label from '../Label';
import { fontSizeStepper, textLabelControls, zoom } from './joditControls';
import './styles/_rich-text-editor.scss';

export type RichTextToolbar = 'full' | 'compact';

const TOOLBARS: Record<RichTextToolbar, string[]> = {
  full: [
    'undo',
    'redo',
    'copyformat',
    'zoom',
    'paragraph',
    'font',
    'fontSizeStepper',
    '|',
    'bold',
    'italic',
    'underline',
    'strikethrough',
    'brush',
    '|',
    'link',
    'image',
    '\n',
    'ul',
    'ol',
    '|',
    'left',
    'center',
    'right',
    'justify',
    '|',
    'lineHeight',
    'outdent',
    'indent',
    'eraser'
  ],
  compact: [
    'undo',
    'redo',
    'font',
    'fontSizeStepper',
    'bold',
    'italic',
    'underline',
    'strikethrough',
    'brush',
    'link',
    'image',
    'ul',
    'left',
    'center',
    'right',
    'justify',
    'lineHeight',
    'outdent',
    'indent',
    'eraser'
  ]
};

interface RichTextEditorProps {
  name: string;
  value: string;
  onChange: (html: string) => void;
  onBlur?: () => void;
  label?: string | ReactNode;
  message?: ReactNode;
  placeholder?: string;
  toolbar?: RichTextToolbar;
  minHeight?: number;
  touched?: boolean;
  error?: string;
  className?: string;
}

const RichTextEditor = ({
  name,
  value,
  onChange,
  onBlur,
  label,
  message,
  placeholder = '',
  toolbar = 'full',
  minHeight = 240,
  touched,
  error,
  className
}: RichTextEditorProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const editorRef = useRef<IJodit | null>(null);
  const onChangeRef = useRef(onChange);
  const onBlurRef = useRef(onBlur);
  onChangeRef.current = onChange;
  onBlurRef.current = onBlur;

  useEffect(() => {
    if (!textareaRef.current) return undefined;
    const config: DeepPartial<Config> = {
      placeholder,
      minHeight,
      buttons: TOOLBARS[toolbar],
      toolbarAdaptive: false,
      toolbarButtonSize: toolbar === 'compact' ? 'small' : 'large',
      controls: {
        fontSizeStepper,
        zoom,
        ...textLabelControls(Jodit.defaultOptions.controls as Record<string, IControlType<IJodit>>)
      } as DeepPartial<Config>['controls'],
      statusbar: false,
      hidePoweredByJodit: true,
      askBeforePasteHTML: false,
      askBeforePasteFromWord: false,
      defaultActionOnPaste: 'insert_clear_html',
      disablePlugins: ['about', 'ai-assistant', 'speech-recognize', 'source', 'print', 'preview', 'fullsize'],
      link: { noFollowCheckbox: false, openInNewTabCheckbox: true },
      uploader: { insertImageAsBase64URI: false }
    };
    const editor = Jodit.make(textareaRef.current, config);
    editor.e.on('change', (html: string) => onChangeRef.current(html));
    editor.e.on('blur', () => onBlurRef.current?.());
    editorRef.current = editor;
    return () => {
      editorRef.current = null;
      editor.destruct();
    };
  }, [placeholder, minHeight, toolbar]);

  useEffect(() => {
    const editor = editorRef.current;
    if (editor && editor.value !== value) editor.value = value ?? '';
  }, [value]);

  return (
    <div className={`rich-text-editor rich-text-editor--${toolbar} ${className ?? ''}`}>
      {label && <Label className="tracking-[1.1375px]" htmlFor={name} label={label} />}
      {message && <div className="mt-2 text-neutral-500">{message}</div>}
      <div className="mt-2">
        <textarea ref={textareaRef} id={name} name={name} defaultValue={value} />
      </div>
      {touched && error && <ErrorText error={error} />}
    </div>
  );
};

export default RichTextEditor;
